import React from "react";
import { fade, makeStyles, useTheme } from "@material-ui/core";
import { StatusType, getStatusIconColor } from "../../../constants/status";
import { getStatusLabel, getStatusStyle, getStatusTooltip, resolveThemeMode } from "../../../utils/statusUtils";
import Chip from "../Chip/Chip";
import StatusIcon from "./StatusIcon";

export interface TestStatusChipProps {
  status: StatusType;
  label?: string;
  tooltip?: string;
  showIcon?: boolean;
  excluded?: boolean;
  className?: string;
  style?: React.CSSProperties;
  /**
   * Force the chip's text/icon/background color, bypassing the Tests-Legend `status` palette.
   * Used where a chip belongs to a different legend (e.g. the Calendar's "Scheduled Items"
   * Due/Completed, which the Scheduled Items Legend renders in the warning/success palette).
   * `text` also drives the icon color; `background` gets a light wash if omitted.
   */
  colorOverride?: { text: string; background?: string };
  /** Set when the caller wraps this chip in its own Tooltip, to avoid a redundant native browser tooltip. */
  disableNativeTitle?: boolean;
}

const useStyles = makeStyles({
  root: {
    maxWidth: 200,
    fontWeight: 600,
  },
  skipped: {
    fontWeight: 400,
    "& .MuiChip-label": {
      fontWeight: 400,
    },
  },
  icon: {
    color: "inherit",
  },
  iconMatchText: {
    display: "inline-flex",
    color: "inherit",
    "& .MuiChip-icon": {
      color: "inherit",
    },
  },
});

function TestStatusChip({ status, label, tooltip, showIcon = true, excluded, className, style, colorOverride, disableNativeTitle }: TestStatusChipProps) {
  const classes = useStyles();
  const theme = useTheme();
  const mode = resolveThemeMode(theme.palette.type);
  const statusStyle = getStatusStyle(status, mode);
  const displayLabel = label ?? getStatusLabel(status);
  const displayTooltip = tooltip ?? getStatusTooltip(status);
  const isSkipped = status === "skipped";
  const textColor = colorOverride?.text ?? statusStyle.color;
  const backgroundColor = colorOverride ? colorOverride.background ?? fade(colorOverride.text, 0.12) : statusStyle.backgroundColor;
  // Non-skipped icons inherit the Chip's `color` (= textColor) via classes.icon's `color: inherit`,
  // so an override text color already tints them; only the skipped branch needs an explicit value.
  const iconColor = isSkipped ? getStatusIconColor(status, mode) : textColor;

  return (
    <Chip
      className={[classes.root, isSkipped ? classes.skipped : undefined, className].filter(Boolean).join(" ")}
      size="small"
      label={displayLabel}
      title={disableNativeTitle ? undefined : displayTooltip}
      aria-label={displayLabel}
      icon={
        showIcon ? (
          isSkipped ? (
            <span className={classes.iconMatchText} style={{ color: iconColor }}>
              <StatusIcon
                status={status}
                excluded={excluded}
                fontSize="small"
                className={classes.icon}
                colored={false}
              />
            </span>
          ) : (
            <StatusIcon status={status} excluded={excluded} fontSize="small" className={classes.icon} colored={false} />
          )
        ) : undefined
      }
      style={{
        backgroundColor,
        color: textColor,
        ...style,
      }}
    />
  );
}

export default TestStatusChip;
